import { useState, ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import OptSectionTabs from './OptSectionTabs';
import { useLanguage } from '@/hooks/useLanguage';
import { useTheme } from '@/hooks/useTheme';
import { useAuth } from '@/hooks/useAuth';
import { isPathAllowedFor } from '@/utils/viewGroups';

interface DashboardLayoutProps {
  children: ReactNode;
  breadcrumb?: string[];
}

export default function DashboardLayout({ children, breadcrumb }: DashboardLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { language, setLanguage } = useLanguage();
  const { isDark } = useTheme();
  const { isAuthenticated, user } = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (mobileOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen, isMobile]);

  // Редизайн: контент/топбар зсунуті на СТАЛІ 60px, тож hover-розкриття сайдбара
  // (overlay) не рухає екран. Поза редизайном — стара логіка згортання.
  const isRd = import.meta.env.VITE_REDESIGN === '1';
  const sidebarWidth = isRd ? 60 : (collapsed ? 60 : 220);

  // Auth gate: no logged-in user (mock login form AND Telegram SSO both populate
  // localStorage 'helios_auth_user') → bounce to /login. Protects the whole
  // cabinet from direct-URL access (was open to "Гість").
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Обмежені групи (напр. технічний диспетчер: лише Дашборд і SCADA) не
  // відкривають інші сторінки навіть прямою адресою — назад на головну.
  if (!isPathAllowedFor(user, pathname)) return <Navigate to="/" replace />;

  return (
    <div
      className="min-h-screen"
      style={{
        background: isRd ? 'var(--bg)' : (isDark ? '#111113' : '#F5F5F7'),
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* Mobile backdrop */}
      {mobileOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        isMobile={isMobile}
        language={language}
      />

      <Topbar
        sidebarWidth={sidebarWidth}
        breadcrumb={breadcrumb}
        language={language}
        onLanguageChange={setLanguage}
        onMobileMenuToggle={() => setMobileOpen(!mobileOpen)}
        isMobile={isMobile}
      />

      <main
        className="pt-[72px] min-h-screen transition-all duration-300"
        style={{ marginLeft: isMobile ? 0 : `${sidebarWidth}px` }}
      >
        {/* Ряд розділів «Оптимізації» (тільки ArtemK, тільки на його п'яти
            маршрутах) стоїть ТУТ, у каркасі: так відступ від шапки однаковий
            на всіх сторінках ряду. Поза ними компонент нічого не рендерить. */}
        <OptSectionTabs />
        <div className="p-4 md:p-6 text-gray-900 dark:text-gray-100">
          {children}
        </div>
      </main>
    </div>
  );
}